import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Logo, LogoWhite } from "@/components/brand/Logo";
import { useApi } from "@/lib/api";
import { fmtCurrency, fmtNum } from "@/lib/format";
import { motion } from "framer-motion";
import {
  ShieldCheck, Wallet, BarChart3, Activity, FileCheck2, Coins, ArrowRight, Check, ArrowUpRight,
  Network, Users2, Fingerprint, GitBranch, LayoutGrid, Eye, Sparkles, FileText, Presentation,
  CalendarRange, Boxes, Circle, TrendingUp, Palette,
  MessageSquare, Bot, Globe, ShieldAlert, Redo2, Route, ReceiptText, CheckCircle2,
} from "lucide-react";

const EASE = [0, 0, 0.35, 1];
const LANDING_THEMES = [
  { key: "lt-sky", name: "Sky", desc: "Light blue", swatch: "linear-gradient(135deg,#dbeafe,#93c5fd)" },
  { key: "lt-slate", name: "Slate", desc: "Cool professional", swatch: "linear-gradient(135deg,#e2e8f0,#94a3b8)" },
  { key: "lt-mint", name: "Mint", desc: "Fresh green", swatch: "linear-gradient(135deg,#d1fae5,#6ee7b7)" },
  { key: "lt-royal", name: "Royal", desc: "Indigo light", swatch: "linear-gradient(135deg,#e0e7ff,#a5b4fc)" },
];
const reveal = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.3 }, transition: { duration: 0.55, ease: EASE } };

const PILLARS = [
  { icon: ShieldCheck, title: "Governance", metric: "policies live", desc: "One rule compiles to the gateway, browser extension, Purview DLP and Entra — simulated before rollout.", featured: true },
  { icon: Activity, title: "Monitoring", metric: "blocked · live", desc: "Every prompt, tool and agent watched in real time, keyed to real people. Shadow-AI caught as it happens." },
  { icon: Wallet, title: "Budgeting", metric: "of budget", desc: "Budgets, chargeback and alerts across every vendor. Route smart, stop overspend before the invoice." },
  { icon: FileCheck2, title: "Compliance", metric: "< 1 day evidence", desc: "Audit-ready evidence mapped to ISO 42001, NIST AI RMF and the EU AI Act — one click, not one quarter." },
  { icon: Coins, title: "Economics", metric: "found", desc: "Surface and capture savings, right-size models, reclaim seats — and report ROI by team and use-case." },
];

const ERAS = [
  { era: "Era 1", range: "2018–2022", title: "Point AI features", desc: "AI bolted onto one app. One console, one owner — narrow but simple.", tone: "muted" },
  { era: "Era 2", range: "2022–2025", title: "AI tool sprawl", desc: "Every team adopts its own AI. Each vendor governs itself; the gaps sit between them.", tone: "warn" },
  { era: "Era 3 · here", range: "2025 →", title: "The AI Governance OS", desc: "One event model, one policy fabric, one ledger, one identity graph — a single window.", tone: "active" },
];

const PRIMITIVES = [
  { icon: Fingerprint, title: "One identity graph", desc: "Every interaction, cost and risk keyed to a real person via Entra ID." },
  { icon: GitBranch, title: "One policy fabric", desc: "Write a rule once; compile it to gateway, extension, Purview DLP and Entra." },
  { icon: Coins, title: "One cost ledger", desc: "Tokens, credits and seats across every vendor, reconciled to the invoice." },
  { icon: LayoutGrid, title: "One event model", desc: "Microsoft, Anthropic, OpenAI, gateways & SSE normalised into one stream." },
];

const FRAMEWORKS = ["ISO/IEC 42001", "NIST AI RMF", "EU AI Act", "ISO 27001", "India DPDP", "GDPR"];

export default function Landing() {
  const [stuck, setStuck] = useState(false);
  const [lt, setLt] = useState(() => localStorage.getItem("navitra-landing-theme") || "lt-sky");
  useEffect(() => { localStorage.setItem("navitra-landing-theme", lt); }, [lt]);
  useEffect(() => { const on = () => setStuck(window.scrollY > 8); on(); window.addEventListener("scroll", on, { passive: true }); return () => window.removeEventListener("scroll", on); }, []);
  const { data: c } = useApi("landing-cockpit", "/cockpit/summary");
  const { data: impact } = useApi("landing-impact", "/analytics/impact", { period: "month" });

  const spendN = c ? c.kpis.spend.value : 34364;
  const blocksN = c ? c.kpis.blocks.value : 66;
  const activeN = c ? c.kpis.active_users.value : 120;
  const savingsN = c ? c.kpis.savings.value : 22350;
  const budgetPctN = c ? c.kpis.spend.pct : 66;

  const spend = fmtCurrency(Math.round(useCountUp(spendN)));
  const blocks = fmtNum(Math.round(useCountUp(blocksN)));
  const active = fmtNum(Math.round(useCountUp(activeN)));
  const savings = fmtCurrency(Math.round(useCountUp(savingsN)));
  const budgetPct = Math.round(useCountUp(budgetPctN));

  return (
    <div className={`${lt} landing-wash min-h-screen relative overflow-x-hidden bg-background text-foreground`}>
      <div className="relative z-[1]">
        {/* ===== Header ===== */}
        <header className={"sticky top-0 z-40 landing-header glass-bar border-b border-border/60 " + (stuck ? "is-stuck" : "")}>
          <div className="max-w-6xl mx-auto flex items-center justify-between px-5 h-16">
            <Logo className="h-16" />
            <nav className="hidden lg:flex items-center gap-1 text-sm">
              {[["Platform", "#platform"], ["Category", "#eras"], ["Impact", "#impact"], ["Walkthrough", "#tour"], ["Compliance", "#compliance"]].map(([l, h]) => (
                <a key={l} href={h} data-testid={`header-nav-${l.toLowerCase()}-link`} className="px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">{l}</a>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              <Link to="/brochure" data-testid="landing-brochure" className="hidden md:inline text-sm px-3 py-1.5 rounded-full hover:bg-secondary transition-colors">Brochure</Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/70 px-3 py-1.5 text-sm hover:border-primary/50 transition-colors" data-testid="landing-theme-trigger" aria-label="Change theme">
                    <Palette className="h-4 w-4 text-primary" /><span className="hidden sm:inline">Theme</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52" data-testid="landing-theme-menu">
                  <DropdownMenuLabel>Home appearance</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {LANDING_THEMES.map((t) => (
                    <DropdownMenuItem key={t.key} onClick={() => setLt(t.key)} data-testid={`landing-theme-${t.key}`} className="gap-2.5 cursor-pointer">
                      <span className="h-5 w-5 rounded-md border border-black/10 shrink-0" style={{ background: t.swatch }} />
                      <span className="flex-1"><span className="text-sm font-medium">{t.name}</span><span className="block text-[11px] text-muted-foreground leading-tight">{t.desc}</span></span>
                      {lt === t.key && <Check className="h-4 w-4 text-primary" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/login" data-testid="landing-signin" className="hidden sm:inline text-sm px-3 py-1.5 rounded-full hover:bg-secondary transition-colors">Sign in</Link>
              <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_10px_30px_-18px_hsl(var(--primary)/0.7)]" data-testid="header-launch-navitra-os-button">
                <Link to="/login">Launch Navitra OS <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
              </Button>
            </div>
          </div>
        </header>

        {/* ===== Hero ===== */}
        <section className="max-w-6xl mx-auto px-5 pt-14 pb-16 grid lg:grid-cols-[1.02fr_0.98fr] gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary)/0.16)] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.28)] px-3 py-1 text-xs font-semibold"><Sparkles className="h-3.5 w-3.5" />A new category · AI Governance OS</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white tracking-wide">EVERY AI. GOVERNED.</span>
            </div>
            <h1 className="font-display font-bold text-[2.6rem] sm:text-6xl leading-[1.02] tracking-tight text-foreground">
              Your enterprise runs AI in <span className="text-muted-foreground/60 line-through decoration-2">twelve consoles.</span>
              <span className="block mt-2 text-[hsl(var(--primary))]">Navitra runs it as one.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Copilot, ChatGPT Enterprise, Claude, Gemini, home-grown agents and shadow AI each hold a slice of the same risk, cost and usage. Navitra is the <b className="text-foreground">operating system</b> that governs, secures and optimises every one — from a single window.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_12px_32px_-18px_hsl(var(--primary)/0.75)]" data-testid="hero-launch-navitra-os-button">
                <Link to="/login">Launch Navitra OS <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full bg-white border-border hover:bg-secondary" data-testid="hero-view-security-brief-button">
                <Link to="/login" className="flex items-center"><Eye className="h-4 w-4 mr-2" />See it in action</Link>
              </Button>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["No setup required", "16 governed roles"].map((s) => <span key={s} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[hsl(var(--primary))]" />{s}</span>)}
            </div>
          </motion.div>

          {/* Cockpit mockup */}
          <motion.div initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1, ease: EASE }} className="relative">
            <motion.div className="absolute -inset-6 bg-[radial-gradient(60%_60%_at_50%_20%,hsl(var(--primary)/0.18),transparent_65%)] blur-2xl rounded-[2rem]" aria-hidden animate={{ opacity: [0.65, 1, 0.65] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} />
            <motion.div animate={{ y: [0, -9, 0] }} transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }} className="relative rounded-[26px] bg-card border border-white/70 shadow-[0_40px_90px_-45px_rgba(13,17,23,0.35)] overflow-hidden">
              <div className="flex items-center gap-2 h-10 px-4 border-b border-border/70">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ef6a5e]" /><span className="h-2.5 w-2.5 rounded-full bg-[#f5be4f]" /><span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--primary))]" />
                <span className="ml-2 text-xs text-muted-foreground truncate">Navitra OS · Cockpit</span>
                <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--primary))]"><motion.span className="h-2 w-2 rounded-full bg-[hsl(var(--primary))]" animate={{ opacity: [1, 0.3, 1], scale: [1, 0.82, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />LIVE</span>
              </div>
              <div className="p-4 sm:p-5">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Total governed AI spend · 30d</p>
                    <p className="font-mono-nav font-bold text-4xl tracking-tight mt-1 text-navy tabular-nums">{spend}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary)/0.16)] text-[hsl(var(--primary))] px-2.5 py-1 text-xs font-semibold"><TrendingUp className="h-3.5 w-3.5" />+2.4%</span>
                </div>
                <Sparkline className="mt-3" />
                <div className="grid grid-cols-2 gap-2.5 mt-4">
                  <StatTile label="Blocked events" value={blocks} Icon={ShieldCheck} tone="#DC2626" badge="secured" />
                  <StatTile label="Active users" value={active} Icon={Users2} tone="#2563EB" badge="+8" />
                  {/* teal summary tile */}
                  <div className="rounded-[18px] bg-[hsl(var(--panel))] text-white p-3.5 flex flex-col justify-between shadow-[0_18px_50px_-30px_hsl(var(--panel)/0.65)]">
                    <div className="flex items-center justify-between"><span className="text-[10.5px] uppercase tracking-wide text-white/70">Identified savings</span><Coins className="h-4 w-4 text-[hsl(var(--panel-accent))]" /></div>
                    <p className="font-mono-nav font-bold text-2xl mt-1 tabular-nums">{savings}</p>
                    <span className="text-[10px] text-white/60 mt-0.5">across 12 recommendations</span>
                  </div>
                  {/* gauge */}
                  <div className="rounded-[18px] bg-secondary/60 border border-border/60 p-3.5 flex items-center gap-3">
                    <Gauge pct={budgetPct} />
                    <div><p className="text-[10.5px] uppercase tracking-wide text-muted-foreground leading-tight">Budget used</p><p className="font-mono-nav font-bold text-lg tabular-nums text-navy">{budgetPct}%</p></div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between">
                  <AvatarStack />
                  <span className="text-[11px] text-muted-foreground">Microsoft · Anthropic · OpenAI</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* ===== One window (convergence) ===== */}
        <section id="oneview" className="max-w-6xl mx-auto px-5 py-16">
          <motion.div {...reveal} className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] font-semibold mb-3">Every AI · one truth</p>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">From scattered AI tools to <span className="text-[hsl(var(--primary))]">one governed view</span>.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mt-3">Each vendor sees only its own slice. Navitra normalises every prompt, cost and risk into a single event model — so one interaction reads as one complete, auditable journey.</p>
          </motion.div>
          <ConvergenceDiagram />
        </section>

        {/* ===== Platform / control plane ===== */}
        <section id="platform" className="max-w-6xl mx-auto px-5 py-16">
          <motion.div {...reveal} className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] font-semibold mb-3">One platform · five disciplines</p>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">One stop for AI <span className="text-[hsl(var(--primary))]">governance</span>.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mt-3">Governance, monitoring, budgeting, compliance and economics — for every AI your enterprise touches, in a single window.</p>
          </motion.div>

          {/* control plane */}
          <motion.div {...reveal} className="rounded-[24px] bg-card border border-border/70 p-5 md:p-8 shadow-[0_24px_60px_-40px_rgba(13,17,23,0.25)] mb-14">
            <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-6 items-center">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">Every AI surface</p>
                <div className="flex flex-wrap gap-2">
                  {["Microsoft Copilot", "ChatGPT Enterprise", "Claude", "Gemini", "Home-grown agents", "Shadow AI"].map((v) => (
                    <span key={v} className="inline-flex items-center gap-1.5 rounded-full bg-secondary/70 border border-border px-3 py-1.5 text-xs font-medium"><Boxes className="h-3.5 w-3.5 text-navy" />{v}</span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="rounded-[20px] bg-[hsl(var(--panel))] text-white px-5 py-4 text-center shadow-lg w-44">
                  <LogoWhite className="h-9 mx-auto mb-2" />
                  <p className="text-sm font-semibold">Navitra OS</p>
                  <p className="text-[11px] text-white/60 leading-tight mt-0.5">Unified control plane</p>
                </div>
              </div>
              <div className="lg:text-right">
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">Every control</p>
                <div className="flex flex-wrap gap-2 lg:justify-end">
                  {[["One policy fabric", GitBranch], ["One cost ledger", Coins], ["One identity graph", Fingerprint], ["Evidence room", FileCheck2]].map(([v, Ic]) => (
                    <span key={v} className="inline-flex items-center gap-1.5 rounded-full bg-secondary/70 border border-border px-3 py-1.5 text-xs font-medium"><Ic className="h-3.5 w-3.5 text-gold" />{v}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* pillars bento */}
          <div data-testid="pillars-section" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PILLARS.map((p, i) => {
              const val = { Governance: `${impact?.governance?.policies_live ?? 2} ${p.metric}`, Monitoring: `${blocks} ${p.metric}`, Budgeting: `${budgetPct}% ${p.metric}`, Compliance: p.metric, Economics: `${savings} ${p.metric}` }[p.title];
              return (
                <motion.div key={p.title} data-testid="pillar-card" {...reveal} transition={{ ...reveal.transition, delay: i * 0.05 }} whileHover={{ y: -4 }}
                  className={"rounded-[20px] bg-card border border-border/70 p-5 flex flex-col shadow-[0_18px_50px_-34px_rgba(13,17,23,0.22)] " + (p.featured ? "sm:col-span-2 lg:col-span-1 lg:row-span-1 ring-1 ring-[hsl(var(--primary)/0.35)]" : "")}>
                  <div className="h-10 w-10 rounded-full bg-[hsl(var(--primary)/0.14)] text-[hsl(var(--primary))] flex items-center justify-center mb-4"><p.icon className="h-5 w-5" /></div>
                  <h3 className="font-display text-lg">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-6 mt-1.5 flex-1">{p.desc}</p>
                  <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold font-mono-nav text-[hsl(var(--primary))]"><Circle className="h-1.5 w-1.5 fill-current" />{val}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ===== Impact highlights band ===== */}
        <section id="impact" className="max-w-6xl mx-auto px-5 py-16">
          <div className="rounded-[26px] bg-[hsl(var(--panel))] text-white p-6 md:p-10 shadow-[0_30px_80px_-45px_hsl(var(--panel)/0.7)] overflow-hidden relative">
            <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full bg-[hsl(var(--primary)/0.18)] blur-3xl" aria-hidden />
            <div className="relative grid lg:grid-cols-[1fr_1fr] gap-8 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--panel-accent))] font-semibold mb-3">Impact Analytics · built in</p>
                <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Prove the value to the board.</h2>
                <p className="text-white/70 mt-3 leading-7 max-w-lg">Navitra measures its own effect on governance, compliance, monitoring, budget and content risk — for any week, month, year or custom range. Export a management PDF or generate an executive ROI deck in one click.</p>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs"><CalendarRange className="h-3.5 w-3.5 text-[hsl(var(--panel-accent))]" />Week · Month · Year · Custom</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs"><FileText className="h-3.5 w-3.5 text-[hsl(var(--panel-accent))]" />PDF report</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs"><Presentation className="h-3.5 w-3.5 text-[hsl(var(--panel-accent))]" />Executive deck</span>
                </div>
                <Button asChild size="lg" className="mt-7 rounded-full bg-white text-[hsl(var(--panel))] hover:bg-white/90" data-testid="impact-open-button">
                  <Link to="/login">Open Impact Analytics <ArrowUpRight className="h-4 w-4 ml-2" /></Link>
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["ROI this period", impact ? `${impact.headline.roi_multiple}x` : "8.4x", "hsl(var(--panel-accent))"],
                  ["Value delivered", impact ? fmtCurrency(impact.headline.roi_value) : "$50,400", "#ffffff"],
                  ["Violations avoided", impact ? fmtNum(impact.compliance.violations_avoided) : "—", "#ffd27f"],
                  ["Compliance score", impact ? `${impact.compliance.score}%` : "96%", "hsl(var(--panel-accent))"],
                ].map(([l, v, col]) => (
                  <div key={l} className="rounded-[18px] bg-[hsl(var(--panel-2))] border border-white/10 p-4">
                    <p className="text-[11px] uppercase tracking-wide text-white/70">{l}</p>
                    <p className="font-mono-nav font-bold text-2xl md:text-3xl mt-1 tabular-nums" style={{ color: col }}>{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== Eras ===== */}
        <section id="eras" className="max-w-6xl mx-auto px-5 py-16">
          <motion.div {...reveal} className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] font-semibold mb-3">A new category</p>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">Three eras of enterprise AI. <span className="text-[hsl(var(--primary))]">We build the third.</span></h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-4">
            {ERAS.map((e, i) => (
              <motion.div key={e.era} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }}
                className={"rounded-[20px] p-6 border bg-card " + (e.tone === "active" ? "border-[hsl(var(--primary)/0.4)] ring-1 ring-[hsl(var(--primary)/0.3)] shadow-md" : "border-border/70")}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono-nav text-muted-foreground">{e.range}</span>
                  <span className={"text-[11px] font-semibold rounded-full px-2.5 py-0.5 " + (e.tone === "active" ? "bg-[hsl(var(--primary))] text-white" : e.tone === "warn" ? "bg-[rgba(217,139,28,0.16)] text-[#9A5A00]" : "bg-secondary text-muted-foreground")}>{e.era}</span>
                </div>
                <h3 className="font-display text-xl">{e.title}</h3>
                <p className="text-sm text-muted-foreground leading-6 mt-3">{e.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ===== Walkthrough ===== */}
        <section id="tour" className="max-w-6xl mx-auto px-5 py-16">
          <motion.div {...reveal} className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] font-semibold mb-3">A real desktop, in your browser</p>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">Not a dashboard. An <span className="text-[hsl(var(--primary))]">operating system</span> for AI.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mt-3">Windowed apps you can drag, resize and maximize · a dock and launcher · switchable themes, layouts and wallpapers.</p>
          </motion.div>
          <div className="relative mx-auto max-w-4xl h-[340px] md:h-[440px]">
            <motion.div {...reveal} className="absolute left-0 bottom-4 w-[56%]" style={{ rotate: -5 }} data-testid="walkthrough-step">
              <OSFrame title="Risk · Guard" color="#DC2626">
                <div className="space-y-1.5">
                  {[["#DC2626", "Source code pasted to blocked AI"], ["#D97706", "Bulk PII upload to tolerated tool"], ["#2563EB", "Prompt-injection blocked"]].map(([c, t]) => (
                    <div key={t} className="flex items-center gap-2 rounded-lg bg-secondary/60 border border-border/60 px-2 py-1.5"><span className="h-2 w-2 rounded-full" style={{ background: c }} /><span className="text-[11px] truncate">{t}</span></div>
                  ))}
                </div>
              </OSFrame>
            </motion.div>
            <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.05 }} className="absolute right-0 bottom-4 w-[56%]" style={{ rotate: 5 }} data-testid="walkthrough-step">
              <OSFrame title="Prompt Studio" color="#B8962E">
                <div className="rounded-lg bg-secondary/60 border border-border/60 p-2 text-[11px] text-muted-foreground">"summarise this vendor contract"</div>
                <div className="mt-2 rounded-lg bg-[hsl(var(--primary)/0.1)] border border-[hsl(var(--primary)/0.25)] p-2 text-[11px]"><b className="text-[hsl(var(--primary))]">Improved</b> — role, constraints & format added, PII masked.</div>
              </OSFrame>
            </motion.div>
            <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="absolute left-1/2 -translate-x-1/2 top-0 w-[64%] z-10" data-testid="walkthrough-step">
              <OSFrame title="Cockpit · Lens" color="#1A3A5C">
                <div className="grid grid-cols-3 gap-2">
                  {[["Spend", spend], ["Active", active], ["Savings", savings]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-secondary/60 border border-border/60 p-2"><p className="text-[9px] uppercase tracking-wide text-muted-foreground">{l}</p><p className="text-sm font-semibold font-mono-nav leading-tight mt-0.5">{v}</p></div>
                  ))}
                </div>
                <div className="mt-2 h-14 rounded-lg bg-secondary/60 border border-border/60 flex items-end gap-0.5 p-1.5">
                  {[40, 55, 48, 70, 60, 82, 68, 90, 74, 62, 78, 58].map((h, i) => <span key={i} className="flex-1 rounded-sm bg-[hsl(var(--primary))]/70" style={{ height: `${h}%` }} />)}
                </div>
              </OSFrame>
            </motion.div>
          </div>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-2">
            {["Windowed apps", "Dock & launcher", "3 OS layouts", "Themes & wallpapers", "Keyboard shortcuts"].map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-card border border-border/70 px-3 py-1.5 text-xs font-medium"><Check className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />{s}</span>
            ))}
          </div>
        </section>

        {/* ===== Primitives (dark) ===== */}
        <section className="bg-[hsl(var(--panel))] text-white py-16">
          <div className="max-w-6xl mx-auto px-5">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--panel-accent))] font-semibold mb-2">What makes it an OS, not another app</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-10">Four primitives. Everything else builds on top.</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {PRIMITIVES.map((p, i) => (
                <motion.div key={p.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.05 }} className="rounded-[20px] border border-white/10 bg-white/5 p-6">
                  <div className="h-10 w-10 rounded-full bg-[hsl(var(--panel-accent))]/15 flex items-center justify-center mb-4"><p.icon className="h-5 w-5 text-[hsl(var(--panel-accent))]" /></div>
                  <h3 className="font-display text-lg text-white">{p.title}</h3>
                  <p className="text-sm text-white/75 leading-6 mt-2">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Compliance ===== */}
        <section id="compliance" data-testid="compliance-section" className="max-w-6xl mx-auto px-5 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...reveal}>
              <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--primary))] font-semibold mb-2">Evidence, not spreadsheets</p>
              <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-4">Prove control to auditors in under a day.</h2>
              <p className="text-muted-foreground mb-6 leading-7">Every signal lands in one event model, mapped to the frameworks that matter — so an audit evidence pack is one click, not one quarter.</p>
              <div className="flex flex-wrap gap-2">{FRAMEWORKS.map((f) => <span key={f} className="rounded-full bg-card border border-border px-3 py-1 text-sm font-mono-nav">{f}</span>)}</div>
            </motion.div>
            <motion.div {...reveal} className="rounded-[24px] bg-card border border-border/70 p-8 shadow-[0_24px_60px_-40px_rgba(13,17,23,0.25)]">
              <div className="grid grid-cols-2 gap-6">
                {[["90%", "fewer sensitive uploads to unsanctioned AI"], ["15–25%", "AI cost per active user reduction"], ["±2%", "ledger reconciliation to invoices"], ["<1 day", "to generate an audit evidence pack"]].map(([k, v]) => (
                  <div key={k}><p className="font-display font-bold text-3xl md:text-4xl text-navy tracking-tight">{k}</p><p className="text-sm text-muted-foreground mt-1">{v}</p></div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ===== CTA ===== */}
        <section className="max-w-6xl mx-auto px-5 pb-24">
          <div className="relative rounded-[28px] bg-[hsl(var(--panel))] text-white p-10 md:p-16 text-center overflow-hidden">
            <div className="absolute -top-24 -right-24 h-64 w-64 bg-[hsl(var(--primary)/0.2)] blur-3xl rounded-full" aria-hidden />
            <LogoWhite className="h-16 mx-auto mb-6 relative" />
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight mb-3 relative">See your entire AI estate in one place.</h2>
            <p className="text-white/70 max-w-xl mx-auto mb-8 relative">Launch the Navitra OS desktop with realistic demo data — governance, cost and security in one window. No setup required.</p>
            <Button asChild size="lg" className="relative rounded-full bg-white text-[hsl(var(--panel))] hover:bg-white/90" data-testid="final-launch-navitra-os-button">
              <Link to="/login">Launch Navitra OS <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
            <p className="text-xs text-white/60 mt-10 relative">Profecia Links Consulting · Pune · Dubai · Dublin — Navitra OS</p>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ---------- widgets ---------- */
const SOURCES = [
  { icon: Bot, title: "Microsoft Copilot", quote: "Grounded in our tenant data.", color: "#2563EB", y: 55 },
  { icon: MessageSquare, title: "ChatGPT Enterprise", quote: "Fast drafting across teams.", color: "#16A34A", y: 158 },
  { icon: Sparkles, title: "Claude Enterprise", quote: "Long-context contract review.", color: "#B8962E", y: 262 },
  { icon: Globe, title: "Shadow AI · browser", quote: "Unsanctioned — until now.", color: "#DC2626", y: 365 },
];
const TIMELINE = [
  { icon: MessageSquare, label: "Prompt sent", src: "Copilot · 09:24", color: "#2563EB" },
  { icon: ShieldAlert, label: "Sensitive data flagged", src: "Guard · 09:24", color: "#DC2626" },
  { icon: Redo2, label: "Redacted & allowed", src: "Policy · 09:24", color: "#7C3AED" },
  { icon: Route, label: "Routed to Tier-2 model", src: "Gateway · 09:24", color: "#0891B2" },
  { icon: ReceiptText, label: "Cost metered", src: "Ledger · 09:25", color: "#B8962E" },
  { icon: FileCheck2, label: "Evidence stored", src: "Lens · 09:25", color: "#16A34A" },
];

function ConvergenceDiagram() {
  return (
    <motion.div {...reveal} className="grid lg:grid-cols-[minmax(0,0.92fr)_150px_minmax(0,1.05fr)] gap-6 lg:gap-2 items-center">
      {/* left: source cards */}
      <div className="flex flex-col gap-3">
        {SOURCES.map((s, i) => (
          <motion.div key={s.title} whileHover={{ y: -3 }} transition={{ duration: 0.18, ease: EASE }}
            className="rounded-[16px] bg-card border border-border/70 shadow-[0_16px_44px_-32px_rgba(13,17,23,0.3)] overflow-hidden"
            style={{ marginLeft: i % 2 ? 0 : 8, marginRight: i % 2 ? 8 : 0 }} data-testid="oneview-source">
            <div className="h-6 flex items-center gap-1.5 px-2.5 border-b border-border/60">
              <span className="h-1.5 w-1.5 rounded-full bg-[#ef6a5e]" /><span className="h-1.5 w-1.5 rounded-full bg-[#f5be4f]" /><span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))]" />
            </div>
            <div className="p-3 flex items-center gap-3">
              <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: s.color }}><s.icon className="h-4.5 w-4.5 text-white" style={{ height: 18, width: 18 }} /></span>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight">{s.title}</p>
                <p className="text-[11px] text-muted-foreground italic leading-tight mt-0.5 truncate">"{s.quote}"</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* center: node + connectors */}
      <div className="relative flex items-center justify-center min-h-[130px]">
        <svg className="hidden lg:block absolute inset-0 w-full h-full" viewBox="0 0 150 420" preserveAspectRatio="none" aria-hidden>
          {SOURCES.map((s) => (
            <path key={s.title} d={`M0,${s.y} C 55,${s.y} 45,210 78,210`} fill="none" stroke={s.color} strokeWidth="2"
              strokeDasharray="6 6" strokeLinecap="round" vectorEffect="non-scaling-stroke" opacity="0.75" />
          ))}
          <path d="M78,210 L150,210" fill="none" stroke="#1A3A5C" strokeWidth="2.5" strokeDasharray="6 6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>
        <div className="relative z-10 flex flex-col items-center">
          <div className="rounded-[18px] bg-[hsl(var(--panel))] text-white px-4 py-3.5 text-center shadow-[0_18px_44px_-24px_hsl(var(--panel)/0.7)] w-[124px]">
            <LogoWhite className="h-8 mx-auto mb-1.5" />
            <p className="text-[13px] font-semibold leading-tight">Navitra OS</p>
            <p className="text-[10px] text-[hsl(var(--panel-accent))] leading-tight mt-0.5">One event.<br />One truth.</p>
          </div>
          <ArrowRight className="hidden lg:block h-5 w-5 text-navy mt-2 absolute -right-1 top-1/2 opacity-0" />
        </div>
      </div>

      {/* right: complete AI view */}
      <div className="rounded-[22px] bg-card border border-border/70 shadow-[0_28px_70px_-42px_rgba(13,17,23,0.32)] overflow-hidden" data-testid="oneview-panel">
        <div className="h-8 flex items-center gap-1.5 px-3 border-b border-border/60">
          <span className="h-2 w-2 rounded-full bg-[#ef6a5e]" /><span className="h-2 w-2 rounded-full bg-[#f5be4f]" /><span className="h-2 w-2 rounded-full bg-[hsl(var(--primary))]" />
          <span className="ml-2 text-[10px] text-muted-foreground">Navitra OS · Event journey</span>
        </div>
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg">Complete AI view</h3>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary)/0.16)] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.28)] px-2.5 py-1 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" />Governed</span>
          </div>
          <div className="relative pl-1">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" aria-hidden />
            <div className="space-y-3">
              {TIMELINE.map((t) => (
                <div key={t.label} className="flex items-center gap-3 relative">
                  <span className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-card z-10" style={{ background: t.color }}><t.icon className="h-4 w-4 text-white" /></span>
                  <div className="flex-1 flex items-center justify-between min-w-0">
                    <span className="text-sm font-medium">{t.label}</span>
                    <span className="text-[11px] text-muted-foreground font-mono-nav">{t.src}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 rounded-xl bg-secondary/60 border border-border/60 p-3 flex items-center gap-2.5">
            <span className="h-8 w-8 rounded-lg bg-navy flex items-center justify-center shrink-0"><BarChart3 className="h-4 w-4 text-white" /></span>
            <p className="text-xs text-muted-foreground leading-tight">From first prompt to audit evidence — <b className="text-foreground">no blind spots</b>, no disconnected tools.</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function StatTile({ label, value, Icon, tone, badge }) {
  return (
    <div className="rounded-[18px] bg-secondary/50 border border-border/60 p-3.5">
      <div className="flex items-center justify-between"><span className="text-[10.5px] uppercase tracking-wide text-muted-foreground leading-tight">{label}</span><Icon className="h-4 w-4" style={{ color: tone }} /></div>
      <div className="flex items-end justify-between mt-1">
        <p className="font-mono-nav font-bold text-2xl tabular-nums text-navy">{value}</p>
        <span className="text-[10px] font-medium rounded-full bg-[hsl(var(--primary)/0.16)] text-[hsl(var(--primary))] px-1.5 py-0.5">{badge}</span>
      </div>
    </div>
  );
}

function useCountUp(target, duration = 1200) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, start;
    const to = Number(target) || 0;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

function Gauge({ pct = 66 }) {
  const size = 46, sw = 6, r = (size - sw) / 2, circ = 2 * Math.PI * r;
  const off = circ - (Math.min(pct, 100) / 100) * circ;
  return (
    <svg width={size} height={size} className="-rotate-90 shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(13,17,23,0.08)" strokeWidth={sw} />
      <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--primary))" strokeWidth={sw} strokeLinecap="round" strokeDasharray={circ}
        initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: off }} transition={{ duration: 1.3, ease: "easeOut" }} />
    </svg>
  );
}

function Sparkline({ className = "" }) {
  const pts = [18, 22, 16, 26, 21, 30, 24, 34, 28, 38, 30, 42];
  const max = Math.max(...pts), w = 260, h = 40;
  const coords = pts.map((p, i) => [(i / (pts.length - 1)) * w, h - (p / max) * h]);
  const d = coords.map((c) => c.join(",")).join(" ");
  const [ex, ey] = coords[coords.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={"w-full h-10 overflow-visible " + className}>
      <defs><linearGradient id="spark" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary)/0.25)" /><stop offset="100%" stopColor="hsl(var(--primary)/0)" /></linearGradient></defs>
      <motion.polygon points={`0,${h} ${d} ${w},${h}`} fill="url(#spark)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }} />
      <motion.polyline points={d} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.4, ease: "easeInOut" }} />
      <motion.circle cx={ex} cy={ey} r="3.2" fill="hsl(var(--primary))"
        initial={{ scale: 0, opacity: 0 }} animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }} transition={{ duration: 1.8, repeat: Infinity, delay: 1.4 }} />
    </svg>
  );
}

function AvatarStack() {
  const cols = ["#1A3A5C", "hsl(var(--primary))", "#B8962E", "#2563EB"];
  return (
    <div className="flex items-center gap-2">
      <div className="flex -space-x-2">
        {cols.map((c, i) => <span key={i} className="h-6 w-6 rounded-full ring-2 ring-card flex items-center justify-center text-[9px] font-semibold text-white" style={{ background: c }}>{"NAVI"[i]}</span>)}
      </div>
      <span className="text-[11px] text-muted-foreground"><b className="text-foreground font-semibold">120</b> active this week</span>
    </div>
  );
}

function OSFrame({ title, color, children }) {
  return (
    <div className="rounded-[16px] bg-card border border-border/70 shadow-[0_30px_70px_-40px_rgba(13,17,23,0.4)] overflow-hidden">
      <div className="h-8 flex items-center gap-1.5 px-3 border-b border-border/70">
        <span className="h-2 w-2 rounded-full bg-[#ef6a5e]" /><span className="h-2 w-2 rounded-full bg-[#f5be4f]" /><span className="h-2 w-2 rounded-full bg-[hsl(var(--primary))]" />
        <span className="ml-2 text-[10px] font-medium truncate flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />{title}</span>
      </div>
      <div className="p-3 text-left">{children}</div>
    </div>
  );
}
